import React, { useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  SimpleGrid,
  Box,
  Text,
  Flex,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { FiUserPlus } from 'react-icons/fi';
import userService from '../services/userService';
import paymentService from '../services/paymentService';
import attendanceService from '../services/attendanceService';
import { fetchCourses } from '../services/courseService';
import axiosInstance from '../services/axiosInstance';

const primaryColor = '#F2C919';
const secondaryColor = '#2C3E50';
const textColor = '#2C3E50';

const generatePassword = (length = 8) => {
  const charset = '0123456789';
  let password = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    password += charset.charAt(Math.floor(Math.random() * n));
  }
  return password;
};

const AddUserModal = ({ isOpen, onClose, onSuccess, initialData = null, registrationId = null }) => {
  const toast = useToast();

  const [user, setUser] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    parentPhoneNumber: '',
    whatsappNumberUser: '',
    whatsappNumberParent: '',
    matiers: [],
    role: 'student',
    inscriptionDate: new Date().toISOString().split('T')[0],
  });

  const [paymentData, setPaymentData] = useState({
    totalAmount: '',
    paidAmount: '',
    numberOfInstallments: 3,
    deadlineDate: '',
    customInstallments: [],
  });

  const [loading, setLoading] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [matiersOptions, setMatiersOptions] = useState([]);
  const [attendanceGroups, setAttendanceGroups] = useState([]);
  const [studentGroupAssignments, setStudentGroupAssignments] = useState([]);

  // Pre-fill from registration data if provided
  useEffect(() => {
    if (isOpen) {
      setUser({
        name: initialData?.studentName || '',
        email: initialData?.email || '',
        phoneNumber: '',
        parentPhoneNumber: initialData?.parentPhone || '',
        whatsappNumberUser: '',
        whatsappNumberParent: '',
        matiers: [],
        role: 'student',
        inscriptionDate: new Date().toISOString().split('T')[0],
      });
      setPaymentData({
        totalAmount: '',
        paidAmount: '',
        numberOfInstallments: 3,
        deadlineDate: '',
        customInstallments: [],
      });
      setErrorMessage('');
      setStudentGroupAssignments([]);
    }
  }, [isOpen]);

  useEffect(() => {
    fetchCoursesNames();
    fetchWhatsAppGroups();
  }, []);

  const fetchCoursesNames = async () => {
    try {
      let data = await fetchCourses();
      data = data.map((el) => ({ label: el.name, value: el.name }));
      setMatiersOptions(data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchWhatsAppGroups = async () => {
    try {
      const groups = await attendanceService.getWhatsAppGroups();
      setAttendanceGroups(groups);
    } catch (error) {
      console.error('Error fetching WhatsApp groups:', error);
    }
  };

  const calculateInstallments = (data) => {
    const { totalAmount, paidAmount, numberOfInstallments, deadlineDate } = data;
    if (!totalAmount || !numberOfInstallments || !deadlineDate) {
      setPaymentData((prev) => ({ ...prev, customInstallments: [] }));
      return;
    }
    const remaining = parseFloat(totalAmount) - (parseFloat(paidAmount) || 0);
    if (remaining <= 0) {
      setPaymentData((prev) => ({ ...prev, customInstallments: [] }));
      return;
    }
    const numInstallments = parseInt(numberOfInstallments);
    const baseAmount = Math.floor((remaining / numInstallments) * 100) / 100;
    const lastAmount = remaining - baseAmount * (numInstallments - 1);
    const today = new Date();
    const deadline = new Date(deadlineDate);
    const intervalMs = (deadline - today) / numInstallments;
    const previews = [];
    for (let i = 0; i < numInstallments; i++) {
      const dueDate = new Date(today.getTime() + intervalMs * (i + 1));
      previews.push({
        number: i + 1,
        amount: (i === numInstallments - 1 ? lastAmount : baseAmount).toFixed(2),
        dueDate: dueDate.toISOString().split('T')[0],
        isPaid: false,
      });
    }
    setPaymentData((prev) => ({ ...prev, customInstallments: previews }));
  };

  const handleCustomInstallmentChange = (index, field, value) => {
    const newInstallments = [...paymentData.customInstallments];
    newInstallments[index][field] = value;
    setPaymentData((prev) => ({ ...prev, customInstallments: newInstallments }));
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhoneNumber = (phone) => {
    if (!phone) return true;
    return /^[0-9]{8,8}$/.test(phone);
  };

  const validateFields = () => {
    if (!user.name || !user.email) {
      setErrorMessage('يجب ملء جميع الحقول المطلوبة');
      return false;
    }
    if (!validateEmail(user.email)) {
      setErrorMessage('عنوان البريد الإلكتروني غير صالح');
      return false;
    }
    if (!validatePhoneNumber(user.phoneNumber)) {
      setErrorMessage('رقم الهاتف يجب ان يتكون من ثمانية ارقام');
      return false;
    }
    if (!validatePhoneNumber(user.parentPhoneNumber)) {
      setErrorMessage('رقم هاتف ولي الأمر يجب ان يتكون من ثمانية ارقام');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    setLoading(true);
    try {
      const response = await userService.addUser({
        email: user.email,
        password: generatePassword(),
        username: user.name,
        phoneNumber: user.phoneNumber,
        parentPhoneNumber: user.parentPhoneNumber,
        whatsappNumberUser: user.whatsappNumberUser,
        whatsappNumberParent: user.whatsappNumberParent,
        matiers: user.matiers || [],
        role: user.role || 'student',
        inscriptionDate: user.inscriptionDate,
      });

      const savedUser = response.user;

      toast({
        title: 'تم الإنشاء بنجاح',
        description: 'تم إنشاء المستخدم الجديد بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // If launched from a registration, mark it as enrolled
      if (registrationId) {
        await axiosInstance.put(`/registrations/${registrationId}/status`, {
          status: 'enrolled',
          notes: `تم إنشاء حساب المستخدم بنجاح (${savedUser?.username || user.name})`,
        });
      }

      // Handle payment creation
      if (savedUser && savedUser.role === 'student' && paymentData.totalAmount) {
        try {
          await paymentService.createPayment({
            studentId: savedUser._id,
            totalAmount: parseFloat(paymentData.totalAmount),
            paidAmount: parseFloat(paymentData.paidAmount) || 0,
            numberOfInstallments: parseInt(paymentData.numberOfInstallments),
            deadlineDate: paymentData.deadlineDate,
            customInstallments: paymentData.customInstallments,
          });
          toast({
            title: 'تم إنشاء خطة الدفع',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } catch (paymentError) {
          console.error('Error with payment plan:', paymentError);
          toast({
            title: 'تحذير',
            description: 'تم حفظ المستخدم ولكن فشل في إنشاء خطة الدفع',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('خطأ:', error.message);
      setErrorMessage(error.message || 'حدث خطأ ما، يرجى المحاولة مرة أخرى');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} dir="rtl" size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" fontFamily="Tajawal, sans-serif">
        <ModalHeader bg={secondaryColor} color="white" borderTopRadius="xl" py={4}>
          <Flex align="center" gap={3}>
            <Icon as={FiUserPlus} boxSize={6} />
            <Text>إضافة مستخدم جديد</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} />

        <ModalBody p={6}>
          <Box>
            {/* Basic Info */}
            <Text fontSize="lg" fontWeight="bold" color={secondaryColor} mb={4}>
              المعلومات الأساسية
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
              <FormControl isRequired>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  اسم المستخدم
                </FormLabel>
                <Input
                  placeholder="أدخل اسم المستخدم"
                  value={user.name}
                  onChange={(e) => setUser({ ...user, name: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  البريد الإلكتروني
                </FormLabel>
                <Input
                  type="email"
                  placeholder="أدخل البريد الإلكتروني"
                  value={user.email}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  رقم الهاتف
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="أدخل رقم الهاتف"
                  value={user.phoneNumber}
                  onChange={(e) => setUser({ ...user, phoneNumber: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  نوع الحساب
                </FormLabel>
                <Select
                  value={user.role}
                  onChange={(e) => setUser({ ...user, role: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                >
                  <option value="student">طالب</option>
                  <option value="instructor">مدرس</option>
                  <option value="admin">مدير</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  تاريخ التسجيل
                </FormLabel>
                <Input
                  type="date"
                  value={user.inscriptionDate}
                  onChange={(e) => setUser({ ...user, inscriptionDate: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.200" />

            {/* Contact Info */}
            <Text fontSize="lg" fontWeight="bold" color={secondaryColor} mb={4}>
              معلومات التواصل الإضافية (للطلاب)
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  رقم هاتف ولي الأمر
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="أدخل رقم هاتف ولي الأمر"
                  value={user.parentPhoneNumber}
                  onChange={(e) => setUser({ ...user, parentPhoneNumber: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  رقم واتساب التلميذ
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="أدخل رقم واتساب التلميذ"
                  value={user.whatsappNumberUser}
                  onChange={(e) => setUser({ ...user, whatsappNumberUser: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  رقم واتساب ولي أمر
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="أدخل رقم واتساب ولي أمر"
                  value={user.whatsappNumberParent}
                  onChange={(e) => setUser({ ...user, whatsappNumberParent: e.target.value })}
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                  borderRadius="lg"
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.200" />

            {/* Matières & Payment */}
            <Text fontSize="lg" fontWeight="bold" color={secondaryColor} mb={4}>
              المواد والمجموعات
            </Text>
            <VStack spacing={6} align="stretch" mb={8}>
              <FormControl>
                <FormLabel color={textColor} fontWeight="600" fontFamily="Tajawal, sans-serif">
                  المواد الدراسية
                </FormLabel>
                <Box>
                  <ReactSelect
                    isMulti
                    options={matiersOptions}
                    placeholder="اختر المواد الدراسية"
                    value={(user.matiers || []).map((el) => ({ label: el, value: el }))}
                    onChange={(selected) =>
                      setUser({ ...user, matiers: selected ? selected.map((item) => item.value) : [] })
                    }
                    styles={{
                      control: (base) => ({
                        ...base,
                        textAlign: 'right',
                        borderColor: '#e2e8f0',
                        borderWidth: '2px',
                        borderRadius: '8px',
                        '&:hover': { borderColor: primaryColor },
                      }),
                      placeholder: (base) => ({ ...base, textAlign: 'right' }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: `${primaryColor}20`,
                        borderRadius: '6px',
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: secondaryColor,
                        fontFamily: 'Cairo, sans-serif',
                      }),
                    }}
                  />
                </Box>
              </FormControl>

              {/* Payment Section for students */}
              {user.role === 'student' && (
                <Box
                  bg="yellow.50"
                  border="2px solid"
                  borderColor="yellow.200"
                  borderRadius="lg"
                  p={4}
                  mt={2}
                >
                  <Text fontWeight="700" color={secondaryColor} mb={4} fontSize="lg" fontFamily="Tajawal, sans-serif">
                    معلومات الدفع
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel color={textColor} fontWeight="600" fontSize="sm">
                          المبلغ الإجمالي (TND)
                        </FormLabel>
                        <Input
                          type="number"
                          placeholder="3000"
                          value={paymentData.totalAmount}
                          onChange={(e) => {
                            setPaymentData({ ...paymentData, totalAmount: e.target.value });
                            calculateInstallments({ ...paymentData, totalAmount: e.target.value });
                          }}
                          bg="white"
                          border="2px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: primaryColor }}
                          borderRadius="lg"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel color={textColor} fontWeight="600" fontSize="sm">
                          المبلغ المدفوع (TND)
                        </FormLabel>
                        <Input
                          type="number"
                          placeholder="1000"
                          value={paymentData.paidAmount}
                          onChange={(e) => {
                            setPaymentData({ ...paymentData, paidAmount: e.target.value });
                            calculateInstallments({ ...paymentData, paidAmount: e.target.value });
                          }}
                          bg="white"
                          border="2px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: primaryColor }}
                          borderRadius="lg"
                        />
                      </FormControl>
                    </SimpleGrid>

                    {paymentData.totalAmount && (
                      <Box
                        bg={primaryColor}
                        color={secondaryColor}
                        p={3}
                        borderRadius="lg"
                        textAlign="center"
                        fontWeight="700"
                      >
                        المبلغ المتبقي:{' '}
                        {(
                          parseFloat(paymentData.totalAmount) - (parseFloat(paymentData.paidAmount) || 0)
                        ).toFixed(2)}{' '}
                        TND
                      </Box>
                    )}

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel color={textColor} fontWeight="600" fontSize="sm">
                          عدد الأقساط
                        </FormLabel>
                        <Input
                          type="number"
                          min="1"
                          value={paymentData.numberOfInstallments}
                          onChange={(e) => {
                            setPaymentData({ ...paymentData, numberOfInstallments: e.target.value });
                            calculateInstallments({ ...paymentData, numberOfInstallments: e.target.value });
                          }}
                          bg="white"
                          border="2px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: primaryColor }}
                          borderRadius="lg"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel color={textColor} fontWeight="600" fontSize="sm">
                          تاريخ غلق الحساب
                        </FormLabel>
                        <Input
                          type="date"
                          value={paymentData.deadlineDate}
                          onChange={(e) => {
                            setPaymentData({ ...paymentData, deadlineDate: e.target.value });
                            calculateInstallments({ ...paymentData, deadlineDate: e.target.value });
                          }}
                          bg="white"
                          border="2px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: primaryColor }}
                          borderRadius="lg"
                        />
                      </FormControl>
                    </SimpleGrid>

                    {paymentData.customInstallments.length > 0 && (
                      <Box bg="white" p={3} borderRadius="lg" border="1px solid" borderColor="gray.200">
                        <Text fontWeight="600" color={textColor} mb={2} fontSize="sm">
                          إدارة الأقساط (Tranches):
                        </Text>
                        <VStack spacing={3} align="stretch">
                          {paymentData.customInstallments.map((inst, idx) => (
                            <Box key={idx} p={3} bg="gray.50" borderRadius="md">
                              <Flex justify="space-between" align="end" gap={2}>
                                <VStack align="start" flex="1">
                                  <Text fontSize="xs" fontWeight="bold">
                                    المبلغ (القسط {idx + 1})
                                  </Text>
                                  <Input
                                    size="sm"
                                    type="number"
                                    value={inst.amount}
                                    onChange={(e) =>
                                      handleCustomInstallmentChange(idx, 'amount', e.target.value)
                                    }
                                    bg="white"
                                  />
                                </VStack>
                                <VStack align="start" flex="1">
                                  <Text fontSize="xs" fontWeight="bold">
                                    تاريخ الدفع
                                  </Text>
                                  <Input
                                    size="sm"
                                    type="date"
                                    value={inst.dueDate}
                                    onChange={(e) =>
                                      handleCustomInstallmentChange(idx, 'dueDate', e.target.value)
                                    }
                                    bg="white"
                                  />
                                </VStack>
                                <Badge colorScheme={inst.isPaid ? 'green' : 'orange'}>
                                  {inst.isPaid ? 'مدفوع' : 'معلق'}
                                </Badge>
                              </Flex>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}

              {errorMessage && (
                <Alert status="error" borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription fontFamily="Tajawal, sans-serif">{errorMessage}</AlertDescription>
                </Alert>
              )}
            </VStack>
          </Box>
        </ModalBody>

        <ModalFooter bg="gray.50" borderBottomRadius="xl">
          <HStack spacing={3}>
            <Button
              bg={primaryColor}
              color={secondaryColor}
              _hover={{ bg: '#d4b016' }}
              onClick={handleSave}
              isLoading={loading}
              loadingText="جاري الحفظ..."
              fontFamily="Tajawal, sans-serif"
            >
              إضافة المستخدم
            </Button>
            <Button variant="ghost" onClick={onClose} fontFamily="Tajawal, sans-serif">
              إلغاء
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddUserModal;
